import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useOptionalSkinSession } from '../../salon/context/SkinSessionContext';
import { SESSION_APP_DATA_BASE } from '../../salon/config/sessionAppData';
import { fetchWithTimeout } from '../../salon/utils/fetchWithTimeout';
import { getSalonAppSessionToken } from '../../salon/utils/salonAppSessionToken';
import { persistFranchiseBrand, readFranchiseBrandFromSession } from '../../shared/franchiseBrand';
import { appendFranchiseThemeParams } from '../../shared/franchiseFlowAuth';
import { readGuestFlowAuth } from '../../shared/guestFlowAuth';
import { readStoredOrgLogo, resolveLogoUrl } from '../utils/faceIdentityBrand';

const HAIR_APP_SESSION_TOKEN_KEY = 'hair_app_session_token';

function readHairAppSessionToken() {
  try {
    return String(sessionStorage.getItem(HAIR_APP_SESSION_TOKEN_KEY) || '').trim();
  } catch {
    return '';
  }
}

function readGuestSessionLogo() {
  const guest = readGuestFlowAuth();
  if (!guest) return '';
  return resolveLogoUrl(guest.franchise_logo || guest.org_logo) || '';
}

export function useFaceIdentityOrgLogo() {
  const location = useLocation();
  const skinSession = useOptionalSkinSession();
  const sessionToken = skinSession?.token || '';
  const tokenReady = skinSession ? skinSession.tokenReady : true;
  const routeToken = location.state?.token || null;
  const token =
    routeToken || sessionToken || getSalonAppSessionToken() || readHairAppSessionToken();

  const [orgLogoUrl, setOrgLogoUrl] = useState(
    () => readGuestSessionLogo() || readStoredOrgLogo(),
  );
  const [logoFailed, setLogoFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadLogo = async () => {
      const guestLogo = readGuestSessionLogo();
      if (guestLogo && !cancelled) {
        setOrgLogoUrl(guestLogo);
        setLogoFailed(false);
        persistFranchiseBrand({
          logo: guestLogo,
          name: readGuestFlowAuth()?.franchise_name,
        });
      }

      const stored = readStoredOrgLogo();
      if (stored && !cancelled && !guestLogo) {
        setOrgLogoUrl(stored);
        setLogoFailed(false);
      }

      const fromSession = readFranchiseBrandFromSession();
      if (fromSession.logo && !cancelled) {
        setOrgLogoUrl(fromSession.logo);
        setLogoFailed(false);
        persistFranchiseBrand(fromSession);
      }

      if (!tokenReady || !token) return;

      try {
        const qs = appendFranchiseThemeParams({ _: String(Date.now()) });
        const pageSetupUrl = `${SESSION_APP_DATA_BASE}/${encodeURIComponent(token)}/page-setup?${qs}`;
        const res = await fetchWithTimeout(pageSetupUrl, { timeoutMs: 15000 });
        if (!res.ok) return;
        const result = await res.json();
        const ext = result?.external_fields || result?.data?.external_fields || {};
        const fromSetup = resolveLogoUrl(ext?.franchise_logo || ext?.org_logo);
        if (fromSetup && !cancelled) {
          setOrgLogoUrl(fromSetup);
          setLogoFailed(false);
          persistFranchiseBrand({
            logo: ext?.franchise_logo || ext?.org_logo,
            name: ext?.organization_name || ext?.org_name || ext?.salon_name,
          });
        }
      } catch (_) {
        /* keep stored fallback */
      }
    };

    void loadLogo();
    return () => {
      cancelled = true;
    };
  }, [token, tokenReady]);

  const showLogo = Boolean(orgLogoUrl) && !logoFailed;

  return {
    orgLogoUrl,
    showLogo,
    onLogoError: () => setLogoFailed(true),
  };
}
